/**
 * Clear Actions Menu Component
 *
 * Provides a dropdown menu with options to clear different types of return data.
 */

import React, { useState } from "react";
import { Menu, Button, Text } from "@mantine/core";
import { IconTrash, IconChevronDown } from "@tabler/icons-react";
import { clearReturnData } from "../../api";
import type { ClearAction } from "../../types";
import { notifications } from "@mantine/notifications";

interface ClearActionsMenuProps {
  returnId: string;
  onClearSuccess?: () => void;
}

export const ClearActionsMenu: React.FC<ClearActionsMenuProps> = ({
  returnId,
  onClearSuccess,
}) => {
  const [isClearing, setIsClearing] = useState(false);

  const handleClear = async (action: ClearAction) => {
    if (
      !window.confirm(
        `Are you sure you want to clear ${action}? This action cannot be undone.`
      )
    ) {
      return;
    }

    setIsClearing(true);
    try {
      const message = await clearReturnData(returnId, action);
      notifications.show({
        title: "Success",
        message: message,
        color: "green",
      });
      onClearSuccess?.();
    } catch (error) {
      notifications.show({
        title: "Error",
        message:
          error instanceof Error ? error.message : "Failed to clear data",
        color: "red",
      });
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <Menu shadow="md" width={200}>
      <Menu.Target>
        <Button
          variant="light"
          color="red"
          leftSection={<IconTrash size={16} />}
          rightSection={<IconChevronDown size={16} />}
          loading={isClearing}
        >
          Clear Data
        </Button>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>
          <Text size="xs">Clear Return Data</Text>
        </Menu.Label>
        <Menu.Item onClick={() => handleClear("state")}>
          <Text size="sm">Clear State</Text>
        </Menu.Item>
        <Menu.Item onClick={() => handleClear("cache")}>
          <Text size="sm">Clear Cache</Text>
        </Menu.Item>
        <Menu.Item onClick={() => handleClear("logs")}>
          <Text size="sm">Clear Timesteps</Text>
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item onClick={() => handleClear("all")} color="red">
          <Text size="sm" fw={600}>
            Clear All
          </Text>
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};
